import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Typography
} from '@mui/material'
import {
  Company,
  CompanyType,
  CompanyUsersStatus
} from '@shared/types/companies.types'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../hooks/useAuthStore'
import { useJoinCompanyMutation } from '../hooks/useMutations/useUsersMutations'
import { useSnackbar } from '../hooks/useSnackbar'
import { LoaderButton } from './LoaderButton'

export default function CompanyCard({
  company
}: {
  company: Company & { status: CompanyUsersStatus | null }
}) {
  const { t } = useTranslation()
  const { userId } = useAuthStore()
  const { showSnackbar } = useSnackbar()

  const joinCompanyMutation = useJoinCompanyMutation()

  return (
    <Card
      elevation={2}
      sx={{
        padding: '1.25rem',
        width: '15rem',
        borderRadius: '.25rem',
        height: '20rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {company.logoUri ? (
        <CardMedia
          component='img'
          image={company.logoUri}
          sx={{ height: '5rem', borderRadius: '.25rem' }}
        />
      ) : (
        <Box
          sx={{
            height: '5rem',
            borderRadius: '.25rem',
            backgroundColor: '#ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography variant='subtitle2' color='white'>
            {company.name?.[0]?.toUpperCase() || '?'}
          </Typography>
        </Box>
      )}

      <CardContent
        sx={{
          flexGrow: 1,
          padding: '1rem 0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          alignSelf: 'stretch'
        }}
      >
        <CardHeader
          sx={{
            padding: '0'
          }}
          title={
            <Typography variant='body2' fontWeight={500}>
              {company.name}
            </Typography>
          }
          subheader={
            <Typography variant='body2' color='text.secondary'>
              {company.type === CompanyType.CONSTRUCTOR
                ? t('common.constructor_type')
                : t('common.provider')}
            </Typography>
          }
        />
        {company.state && company.country ? (
          <Typography variant='body2'>
            {company.state}, {company.country}.
          </Typography>
        ) : (
          <Typography
            variant='body2'
            color='text.disabled'
            sx={{ fontStyle: 'italic' }}
          >
            {t('companies_directory.address_empty')}
          </Typography>
        )}
        {company.webUrl ? (
          <Typography variant='body2'>{company.webUrl}</Typography>
        ) : (
          <Typography
            variant='body2'
            color='text.disabled'
            sx={{ fontStyle: 'italic' }}
          >
            {t('companies_directory.web_empty')}
          </Typography>
        )}
      </CardContent>

      <LoaderButton
        loading={joinCompanyMutation.isPending}
        variant='contained'
        disabled={!!company.status || joinCompanyMutation.isPending}
        sx={{
          textTransform: 'none'
        }}
        onClick={() =>
          joinCompanyMutation.mutate(
            {
              userId: userId!,
              companyId: company.id
            },
            {
              onSuccess: () => {
                showSnackbar({
                  message: t('companies_directory.join.success', {
                    companyName: company.name
                  }),
                  severity: 'info'
                })
              },
              onError: () => {
                showSnackbar({
                  message: t('companies_directory.join.error'),
                  severity: 'error'
                })
              }
            }
          )
        }
        label={
          company.status !== null &&
          [
            CompanyUsersStatus.PENDING_USER,
            CompanyUsersStatus.PENDING_COMPANY
          ].includes(company.status)
            ? t('companies_directory.pending')
            : t('companies_directory.request')
        }
      />
    </Card>
  )
}
